/**
 * MilestoneTimeline - Horizontal timeline showing workflow milestones
 */
import React from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { cn } from '../../lib/utils';
import { milestoneStatuses } from './constants';

const MilestoneTimeline = ({ workflow, onMilestoneClick, isUpdating }) => {
  const milestones = (workflow.milestones || []).sort((a, b) => (a.order || 0) - (b.order || 0));
  
  if (milestones.length === 0) {
    return <p className="text-sm text-muted-foreground">No milestones</p>;
  }

  const calculateMilestoneDate = (dueDays) => {
    const photoDate = workflow.photography_date || workflow.shoot_date;
    if (!photoDate || dueDays === undefined || dueDays === null) return null;
    const baseDate = new Date(photoDate);
    if (isNaN(baseDate.getTime())) return null;
    const msDate = new Date(baseDate);
    msDate.setDate(msDate.getDate() + dueDays);
    return msDate;
  };

  const isOverdue = (msDate, status) => {
    if (!msDate || status === 'completed' || status === 'skipped') return false;
    return new Date() > msDate;
  };

  const isNearDue = (msDate, status) => {
    if (!msDate || status === 'completed' || status === 'skipped') return false;
    const now = new Date();
    const diffDays = Math.ceil((msDate - now) / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays <= 3;
  };

  return (
    <div className="relative">
      {!workflow.photography_date && !workflow.shoot_date && (
        <p className="text-xs text-orange-600 mb-2">⚠ Set a photography date to see milestone due dates</p>
      )}
      
      <div className="absolute top-4 left-4 right-4 h-1 bg-gray-200 dark:bg-gray-700 rounded" />
      
      <div 
        className="absolute top-4 left-4 h-1 bg-green-500 rounded transition-all"
        style={{ 
          width: `${Math.max(0, (milestones.filter(m => m.status === 'completed').length / milestones.length) * 100 - 2)}%`
        }}
      />
      
      <div className="flex justify-between relative">
        {milestones.map((ms, idx) => {
          const statusInfo = milestoneStatuses.find(s => s.value === ms.status) || milestoneStatuses[0];
          const StatusIcon = statusInfo.icon;
          const msDate = calculateMilestoneDate(ms.due_days);
          const isBeforeShoot = ms.due_days < 0;
          const overdue = isOverdue(msDate, ms.status);
          const nearDue = isNearDue(msDate, ms.status);
          
          return (
            <TooltipProvider key={ms.milestone_id || idx}>
              <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => onMilestoneClick(workflow.workflow_id, ms.milestone_id, ms.status, ms)}
                    disabled={isUpdating}
                    className={cn(
                      "flex flex-col items-center gap-1 p-1.5 rounded-lg transition-all hover:bg-muted/50",
                      isUpdating && "opacity-50 cursor-not-allowed"
                    )}
                    data-testid={`milestone-btn-${ms.milestone_id}`}
                  >
                    <div className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center border-2 bg-background z-10 transition-colors",
                      ms.status === 'completed' ? "border-green-500 bg-green-50 dark:bg-green-950/30" : 
                      ms.status === 'in_progress' ? "border-blue-500 bg-blue-50 dark:bg-blue-950/30" : 
                      overdue ? "border-red-500 bg-red-50 dark:bg-red-950/30" :
                      nearDue ? "border-amber-500 bg-amber-50 dark:bg-amber-950/30" :
                      "border-gray-300 bg-gray-50 dark:bg-gray-900"
                    )}>
                      <StatusIcon className={cn(
                        "w-4 h-4",
                        ms.status === 'completed' ? "text-green-600" :
                        ms.status === 'in_progress' ? "text-blue-600" :
                        overdue ? "text-red-600" :
                        nearDue ? "text-amber-600" :
                        "text-gray-400"
                      )} />
                    </div>
                    <span className={cn(
                      "text-xs max-w-[60px] truncate font-medium",
                      ms.status === 'completed' && "line-through text-muted-foreground"
                    )}>
                      {ms.name}
                    </span>
                    {msDate && (
                      <span className={cn(
                        "text-[10px] font-medium",
                        overdue ? "text-red-600" :
                        nearDue ? "text-amber-600" :
                        isBeforeShoot ? "text-orange-600" : "text-muted-foreground"
                      )}>
                        {msDate.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', timeZone: 'Australia/Sydney' })}
                      </span>
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent 
                  side="top" 
                  className="bg-white dark:bg-gray-900 border shadow-lg p-3 rounded-lg max-w-[220px]"
                >
                  <div className="space-y-2">
                    <p className="font-semibold text-sm text-foreground">{ms.name}</p>
                    
                    <div className={cn(
                      "inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium",
                      ms.status === 'completed' ? "bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300" :
                      ms.status === 'in_progress' ? "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300" :
                      overdue ? "bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300" :
                      nearDue ? "bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300" :
                      "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                    )}>
                      <StatusIcon className="w-3 h-3" />
                      {overdue ? "Overdue" : nearDue ? "Due Soon" : statusInfo.label}
                    </div>
                    
                    {msDate && (
                      <p className="text-xs text-muted-foreground">
                        📅 Due: {msDate.toLocaleDateString('en-AU', { weekday: 'short', day: '2-digit', month: 'short', timeZone: 'Australia/Sydney' })}
                      </p>
                    )}
                    
                    {ms.completed_at && (
                      <p className="text-xs text-green-600 dark:text-green-400">
                        ✓ Completed: {new Date(ms.completed_at).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', timeZone: 'Australia/Sydney' })}
                      </p>
                    )}
                    
                    <p className="text-[10px] text-muted-foreground pt-1 border-t border-border">
                      Click to toggle status
                    </p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        })}
      </div>
    </div>
  );
};

export default MilestoneTimeline;
